import { UI_KIT_STORE_PREFIX } from '@rshb/rshb-vue-kit/src/const'
import { SocialService } from '.'
import {
  COOKIE_TOKEN_KEY,
  COOKIE_PATH,
  COOKIE_TOKEN_MAX_AGE_SECONDS,
  COOKIE_UNIQUE_TOKEN,
  COOKIE_UNIQUE_TOKEN_KEY,
  COOKIE_REFRESH_TOKEN_KEY,
  COOKIE_UPDATE_REFRESH_TOKEN_KEY
} from '~/shared/const'
import { UserModel } from '~/model/User'
import { ApiService } from '~/shared/api'
import type { Logger, Nullable } from '~/shared/interfaces'
import { RequestService } from '~/services/RequestService'
import { RequestException } from '~/types/exceptions'
import { isUnknownObject } from '~/shared/utils/guards'
import { RefreshTokenModel } from '~/model/Response'

export default class Service extends ApiService {
  private token: string | null = null
  private refreshPromise: Nullable<Promise<Nullable<RefreshTokenModel>>> = null
  protected route = 'users-management'
  public socialService: SocialService

  constructor(
    requestService: RequestService,
    private cookies: any,
    private currentRoute: any /** @deprecated удалить после замены методов getUser и getResume */,
    private store: any,
    private config: any,
    private logger: Logger
  ) {
    super(requestService)
    this.socialService = new SocialService(requestService)
  }

  /** С throwOnTransientError null означает только 401: сбой связи пробрасывается */
  public getAuthorizedUser(options: { throwOnTransientError?: boolean } = {}): Promise<Nullable<UserModel>> {
    return this.requestService
      .request({
        url: `${this.prefix}/${this.route}/users/sf/authorize`,
        method: 'POST',
        headers: {
          'Authorization-SF-Token': this.getCookieToken()
        },
        _silent: options.throwOnTransientError
      })
      .then(({ data }) => {
        if (isUnknownObject(data) && isUnknownObject(data.data)) {
          return new UserModel(data.data)
        }
        return null
      })
      .catch(error => {
        if (!options.throwOnTransientError || this.getErrorStatus(error) === 401) {
          return null
        }

        throw error
      })
  }

  /**
   * @deprecated
   * перенести метод в другой модуль
   */
  public async getResume(): Promise<any> {
    try {
      const token = this.currentRoute.query.token || this.cookies.get(COOKIE_TOKEN_KEY)

      await this.store.dispatch(`${UI_KIT_STORE_PREFIX}/recruitment/user/getUserResume`, {
        token,
        apiHost: this.config.hosts.externalHost
      })

      return await this.store.getters[`${UI_KIT_STORE_PREFIX}/recruitment/user/authUserResume`]
    } catch (e) {
      console.warn(e) // eslint-disable-line no-console
    }
  }

  /** Refresh-токен одноразовый, поэтому обновление single-flight */
  public getAccessTokenByRefresh(): Promise<Nullable<RefreshTokenModel>> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null
      })
    }

    return this.refreshPromise
  }

  private performRefresh(): Promise<Nullable<RefreshTokenModel>> {
    let refreshToken: string = this.getRefreshCookieToken()
    const updateRefreshToken: string = this.getUpdateRefreshCookieToken()

    if (!refreshToken && updateRefreshToken) {
      this.updateRefreshToken(updateRefreshToken)
      refreshToken = updateRefreshToken
    }

    if (!refreshToken) {
      return Promise.resolve(null)
    }

    return this.requestService
      .request({
        url: `${this.config.hosts.authHost}/api/ext/rshb-auth/refresh`,
        method: 'POST',
        data: {
          refreshToken
        },
        _isRefreshRequest: true,
        _silent: true
      })
      .then(({ data }) => {
        const tokens = isUnknownObject(data) && isUnknownObject(data.result) ? new RefreshTokenModel(data.result) : null

        if (!tokens || !tokens.accessToken) {
          this.logger.error('Обновление токенов отклонено, в ответе нет токена')
          return null
        }

        return tokens
      })
      .catch(error => {
        if (this.getErrorStatus(error) === 401) {
          this.logger.warn('SSO отклонил refresh-токен', error)
        } else {
          this.logger.error('Не удалось обновить токены', error)
        }

        return null
      })
  }

  private getErrorStatus(error: unknown): Nullable<number> {
    return error instanceof RequestException ? error.internalError.status : null
  }

  public getCookieToken(): string {
    if (process.server && this.token) {
      return this.token
    }

    return this.cookies.get(COOKIE_TOKEN_KEY) || this.token
  }

  public getRefreshCookieToken(): string {
    return this.cookies.get(COOKIE_REFRESH_TOKEN_KEY) || ''
  }

  public getUpdateRefreshCookieToken(): string {
    return this.cookies.get(COOKIE_UPDATE_REFRESH_TOKEN_KEY) || ''
  }

  public setCookieToken(token): void {
    this.token = token
    this.cookies.set('token', token, {
      path: COOKIE_PATH,
      maxAge: COOKIE_TOKEN_MAX_AGE_SECONDS
    })
  }

  public getCookieUniquePersonToken(): string {
    return this.cookies.get(COOKIE_UNIQUE_TOKEN_KEY)
  }

  public setCookieUniquePersonToken(): void {
    this.cookies.set(COOKIE_UNIQUE_TOKEN_KEY, COOKIE_UNIQUE_TOKEN)
  }

  public setCookieRefreshToken(token): void {
    if (!token) {
      return
    }

    this.cookies.set('refresh_token', token, {
      path: COOKIE_PATH,
      maxAge: COOKIE_TOKEN_MAX_AGE_SECONDS
    })
  }

  public updateRefreshToken(token): void {
    this.setCookieRefreshToken(token)
    this.cookies.remove(COOKIE_UPDATE_REFRESH_TOKEN_KEY)
  }

  public removeToken(): void {
    this.token = null
    this.cookies.remove(COOKIE_TOKEN_KEY)
  }

  public logout(): void {
    const authHostLogoutUrl = this.config.hosts.authHostLogoutUrl
    const externalHost = this.config.hosts.externalHost

    this.removeToken()
    this.cookies.remove(COOKIE_REFRESH_TOKEN_KEY)
    this.cookies.remove(COOKIE_UPDATE_REFRESH_TOKEN_KEY)

    if (process.client) {
      location.href = `${authHostLogoutUrl}?backurl=${externalHost}/`
    }
  }
}
